import React from 'react';
import { NavLink } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { StoreState } from '../../types/store.type';
import { RequiredActions } from 'guest-data-types-ts-lib';
import { AppRoutes } from '../../routes';
import _ from 'lodash';

import './SideBar.scss';
import { ReservationUtils, TranslationUtils } from '../../utils';
import { NavbarLink } from '../../types/navbar.type';
import { AdjustmentConfig, Splits } from '../../types/split.type';
import { isFeatureAvailableForChannel, useFeatureConfig } from '../../services/optimizely/optimizelyHelper';

export const SideBar = () => {
    const { reservation, guests, required_actions, stay } = useSelector((store: StoreState) => store.trip);

    const {
        isEnabled: isManagePetsAvailable,
        config: managePetsConfig,
        clientReady: managePetClientReady,
    } = useFeatureConfig<AdjustmentConfig>(Splits.GUEST_PORTAL_NEW_PET_ADJUSTMENT, false);

    let isExtendedStayAllowed = stay?.extend_stay_eligible;

    let guestName, confirmationCode;

    if (guests) {
        const { first_name, last_name } = guests.primary;
        guestName = `${first_name} ${last_name}`;
    }

    if (reservation) {
        confirmationCode = reservation.confirmation_code
            ? ReservationUtils.formatConfirmationCode(reservation.confirmation_code)
            : reservation.id;
    }

    const isNewAddPetAvailable = isFeatureAvailableForChannel(reservation!, managePetClientReady, managePetsConfig, isManagePetsAvailable);
    const isPetAdjustmentAllowed = stay?.pets_adjustment_available && isManagePetsAvailable && isNewAddPetAvailable;

    const getReservationNotifications = (requiredActions?: RequiredActions): number => {
        if (!requiredActions) {
            return 0;
        }

        return _.reduce(requiredActions, (sum, value) => (value !== undefined && !value.completed ? sum + 1 : sum), 0);
    };

    const options: NavbarLink[] = [
        {
            id: 'link-reservation',
            displayText: TranslationUtils.get('trip_overview_capitalized'),
            notifications: getReservationNotifications(required_actions),
            route: AppRoutes.RESERVATION,
        },
        {
            id: 'link-modify-trip',
            displayText: TranslationUtils.get('manage_guests_capitalized'),
            notifications: 0,
            route: AppRoutes.MODIFY_TRIP,
            indented: true,
        },
        {
            id: 'link-view-receipt',
            displayText: TranslationUtils.get('view_receipt_capitalized'),
            notifications: 0,
            route: AppRoutes.RECEIPT,
            indented: true,
        },
        {
            id: 'link-cancel-your-trip',
            displayText: TranslationUtils.get('cancel_trip_title'),
            notifications: 0,
            route: AppRoutes.CANCEL,
            indented: true,
        },
        {
            id: 'link-getting-there',
            displayText: TranslationUtils.get('getting_there_capitalized'),
            notifications: 0,
            route: AppRoutes.GETTING_THERE,
        },
        {
            id: 'link-in-the-home',
            displayText: TranslationUtils.get('features_and_amenities_capitalized'),
            notifications: 0,
            route: AppRoutes.IN_THE_HOME,
        },
        {
            id: 'link-checkout-instructions',
            displayText: TranslationUtils.get('checkout_instructions_capitalized'),
            notifications: 0,
            route: AppRoutes.CHECKOUT_INSTRUCTIONS,
        },
    ];

    if (isExtendedStayAllowed) {
        options.splice(1, 0, {
            id: 'link-extended-stay',
            displayText: TranslationUtils.get('extended_stay_capitalized'),
            notifications: 0,
            route: AppRoutes.EXTENDED_STAY,
            indented: true,
        });
    }

    if (isPetAdjustmentAllowed) {
        options.splice(3, 0, {
            id: 'link-manage-pets',
            displayText: TranslationUtils.get('manage_pets_title'),
            notifications: 0,
            route: AppRoutes.MODIFY_PETS,
            indented: true,
        });
    }

    return (
        <div className="sidebar-container d-none d-lg-block">
            <div className="sidebar-data" tabIndex={0}>
                <div className="heading-caps-small-light my-trip">{TranslationUtils.get('my_trip')}</div>
                <div>
                    <b className="body-bold">{guestName ? guestName : ''}</b>
                </div>
                <div>
                    {TranslationUtils.get('confirmation')} : <b className="body-bold">{confirmationCode ? confirmationCode : ''}</b>
                </div>
            </div>
            <hr />
            <ul className="side-navlist">
                {options.map((item, index) => (
                    <li key={index}>
                        <NavLink to={item.route} id={item.id}>
                            <div className={`side-navlist-item ${item.indented ? 'indented' : ''}`}>
                                <p className="body-regular side-option"> {item.displayText}</p>
                                {item.notifications > 0 && <div className="side-notification">{item.notifications}</div>}
                            </div>
                        </NavLink>
                    </li>
                ))}
            </ul>
        </div>
    );
};
